/**
 * Cross-document model primitives — the pieces every assembled document
 * (invoice, income statement, balance sheet, tax report) carries.
 *
 * Isomorphic by construction: no browser globals, no i18n singleton, no
 * TanStack Query, no Sentry. Everything a template needs is either passed in
 * (language, `now`) or fetched through an injected client.
 */
import { z } from "zod";
import { localeForLanguage } from "../format/locale.js";
/**
 * The document/UI languages the app ships translations for. Single source of
 * truth is `DocumentLanguage` in `../format/locale.ts`; the `satisfies` keeps
 * the two from drifting (a new language must be added in both places or this
 * file stops compiling).
 */
export const documentLanguageSchema = z.enum(["en", "fr", "nl"]);
/**
 * Envelope metadata stamped on every document.
 *
 * `locale` is derived from `language` (never from the currency — see
 * `localeForLanguage`) so the template formats numbers without knowing how the
 * mapping works, and `generatedAt` is an ISO instant supplied by the caller
 * rather than read from the clock inside the assembler (deterministic tests).
 */
export const documentMetaSchema = z.object({
    /** ISO-8601 instant the document was assembled. */
    generatedAt: z.string().datetime(),
    /** Requested document language. */
    language: documentLanguageSchema,
    /** BCP-47 number/date formatting locale derived from `language`. */
    locale: z.string(),
    orgId: z.string().uuid(),
    /** IANA timezone from `organizations.timezone`, null when unset. */
    orgTimezone: z.string().nullable(),
    /** The org's ledger/base currency (`organizations.currency`). */
    baseCurrency: z.string(),
});
/**
 * A logo reference. `storagePath` is the private-bucket path (`files` bucket)
 * — web previews resolve it to a signed URL themselves; `dataUri` is the
 * inlined `data:` payload the server-side renderer needs (Chromium runs with
 * every non-`data:` request aborted).
 */
export const logoRefSchema = z.object({
    storagePath: z.string().nullable(),
    dataUri: z.string().nullable(),
});
/** `legal_forms` join on the business-identity row. */
export const legalFormRefSchema = z.object({
    code: z.string(),
    name: z.string(),
    country_code: z.string().nullable(),
});
/**
 * The document-relevant subset of `organization_business_identity` (+ its
 * `legal_forms` join). Fields the templates never print (sector, subsidy,
 * reporting standard, board remuneration, timestamps) are deliberately absent.
 *
 * `is_tax_exempt` is nullable in the database; the assembler normalizes a
 * missing row / null to `false` so templates can branch on a plain boolean.
 */
export const orgBusinessIdentitySchema = z.object({
    registration_number: z.string().nullable(),
    tax_entity_number: z.string().nullable(),
    website_url: z.string().nullable(),
    contact_email: z.string().nullable(),
    registered_address: z.string().nullable(),
    postal_address: z.string().nullable(),
    iban: z.string().nullable(),
    bic: z.string().nullable(),
    is_tax_exempt: z.boolean(),
    legalForm: legalFormRefSchema.nullable(),
});
/**
 * Everything a document header needs about the issuing organization: display
 * name, country, logo, business identity, and the resolved tax identity
 * (jurisdiction + its localized name, e.g. NL → "BTW").
 */
export const orgIdentityBlockSchema = z.object({
    name: z.string(),
    country: z.string().nullable(),
    logo: logoRefSchema,
    identity: orgBusinessIdentitySchema,
    /** `organization_tax_settings.tax_identification_number`. */
    taxIdentificationNumber: z.string().nullable(),
    /** Resolved jurisdiction code (settings first, then org country). */
    taxJurisdiction: z.string().nullable(),
    /** Localized tax abbreviation for the jurisdiction ("BTW", "TVA", "VAT"…). */
    taxName: z.string(),
});
/** An empty business-identity block — the shape used when no row exists yet. */
export const EMPTY_ORG_BUSINESS_IDENTITY = {
    registration_number: null,
    tax_entity_number: null,
    website_url: null,
    contact_email: null,
    registered_address: null,
    postal_address: null,
    iban: null,
    bic: null,
    is_tax_exempt: false,
    legalForm: null,
};
/** Build the `meta` envelope for a document. */
export function buildDocumentMeta({ orgId, orgTimezone, baseCurrency, language, now, }) {
    return {
        generatedAt: now.toISOString(),
        language,
        locale: localeForLanguage(language),
        orgId,
        orgTimezone,
        baseCurrency,
    };
}
/**
 * The record a document is about does not exist, or is not visible to this
 * client (another org's id, or an RLS denial). The two are deliberately
 * indistinguishable — "exists, but not yours" leaks across the tenant
 * boundary. The API maps this to a 404. Sibling of the invoice-specific
 * `InvoiceNotFoundError` (`./invoice/assemble.ts`), for every other document.
 */
export class DocumentNotFoundError extends Error {
    /** What was looked up, e.g. `"project"`. */
    entity;
    orgId;
    id;
    constructor(params) {
        super(`${params.entity} ${params.id} not found in organization ${params.orgId}`);
        this.name = "DocumentNotFoundError";
        this.entity = params.entity;
        this.orgId = params.orgId;
        this.id = params.id;
    }
}
//# sourceMappingURL=common.js.map